import React from 'react'
import Text, { TextScale } from 'particles/Text'
import styled from 'particles/themes/styled'
import Checkbox, { CheckboxSize } from '.'
import CommentContainer from 'components/Shared/Comment'
import Label, { LabelSize, LinkConfig } from 'particles/Label'
import { useField, useFormikContext } from 'formik'
import _ from 'lodash'

type CheckboxGroupOrientation = 'vertical' | 'horizontal'
type CheckboxGroupSize = 'm' | 's'

export interface OptionState<T> {
    label: string
    value: T
}

interface CheckboxGroupProps<T> {
    name: string
    orientation?: CheckboxGroupOrientation
    size?: CheckboxGroupSize
    isRequired?: boolean
    title?: string
    subtitle?: string
    isDisabled?: boolean
    isReadOnly?: boolean
    options: OptionState<T>[]
    selectedOptions?: T[]
    onClick?: (value: T) => void
    errorMessage?: string
    identifier?: string
    dataTestId?: string
    labelSize?: LabelSize
    link?: LinkConfig
    tooltip?: string
}

interface CheckboxGroupContainerProps {
    size: CheckboxGroupSize
}

const CheckboxGroupContainer = styled.div<CheckboxGroupContainerProps>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing[200]};
    .checkboxGroup-label {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: ${({ theme }) => theme.spacing[100]};
        align-self: stretch;
    }
    .checkboxGroup-title {
        display: flex;
        align-items: flex-start;
        gap: ${({ theme }) => theme.spacing[100]};
        align-self: stretch;
    }
    .checkboxes {
        display: flex;
        align-items: flex-start;
        align-self: stretch;
    }
    .checkboxes-vertical {
        flex-direction: column;
        gap: ${({ theme, size }) =>
            ({
                m: theme.spacing[300],
                s: theme.spacing[200],
            })[size]};
    }
    .checkboxes-horizontal {
        align-content: flex-start;
        gap: ${({ theme, size }) =>
            ({
                m: `${theme.spacing[300]} ${theme.spacing[400]}`,
                s: `${theme.spacing[200]} ${theme.spacing[300]}`,
            })[size]};
        flex-wrap: wrap;
    }
    .checkboxes-section {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: ${({ theme }) => theme.spacing[100]};
        align-self: stretch;
    }
`

const sizeMap: Record<
    CheckboxGroupSize,
    { titleTextScale: TextScale; subtitleTextScale: TextScale; checkboxSize: CheckboxSize }
> = {
    m: {
        titleTextScale: 'bodyM500',
        subtitleTextScale: 'bodyS400',
        checkboxSize: 'm',
    },
    s: {
        titleTextScale: 'bodyS500',
        subtitleTextScale: 'caption400',
        checkboxSize: 's',
    },
}

const CheckboxGroup = <T extends string | number>({
    name,
    title,
    subtitle,
    size = 'm',
    orientation = 'vertical',
    isRequired,
    isDisabled,
    isReadOnly,
    options,
    selectedOptions,
    onClick,
    errorMessage,
    identifier,
    dataTestId,
    labelSize,
    link,
    tooltip,
    ...props
}: CheckboxGroupProps<T>) => {
    const groupSize = sizeMap[size]
    let isSelectedEmpty = true
    return (
        <CheckboxGroupContainer size={size}>
            {(title || subtitle) && (
                <CommentContainer identifier={identifier}>
                    <Label
                        title={title || ''}
                        subtext={subtitle}
                        isRequired={isRequired}
                        className="checkboxGroup-label"
                        size={labelSize}
                        link={link}
                        tooltip={tooltip}
                    />
                </CommentContainer>
            )}

            <div className="checkboxes-section">
                <div
                    className={`checkboxes ${
                        orientation === 'horizontal' ? 'checkboxes-horizontal' : 'checkboxes-vertical'
                    }`}
                >
                    {options.map((option, index) => {
                        const isOptionSelected = selectedOptions?.some(selected => _.isEqual(selected, option.value))
                        isSelectedEmpty = isOptionSelected ? false : isSelectedEmpty

                        // If the group is readonly and nothing is selected. Show an empty state
                        if (isReadOnly && isSelectedEmpty && index === options.length - 1) {
                            return (
                                <Checkbox
                                    {...props}
                                    name={name}
                                    key={`${name}-checkboxGroup-${index}`}
                                    label={option.label}
                                    checkbox={isOptionSelected ? 'selected' : 'notSelected'}
                                    onChange={(): void => onClick && onClick(option.value)}
                                    isDisabled={isDisabled}
                                    size={groupSize.checkboxSize}
                                    isReadOnly={isReadOnly}
                                />
                            )
                        }

                        return (isReadOnly && isOptionSelected) || !isReadOnly ? (
                            <Checkbox
                                {...props}
                                key={`checkboxGroup-${index}`}
                                dataTestId={`${dataTestId}-${option.value}`}
                                label={option.label}
                                checkbox={isOptionSelected ? 'selected' : 'notSelected'}
                                onChange={(): void => onClick && onClick(option.value)}
                                isDisabled={isDisabled}
                                size={groupSize.checkboxSize}
                                isReadOnly={isReadOnly}
                            />
                        ) : null
                    })}
                </div>
                {!isReadOnly && !isDisabled && errorMessage && (
                    <Text scale="caption400" color="error">
                        {errorMessage}
                    </Text>
                )}
            </div>
        </CheckboxGroupContainer>
    )
}
interface FormikCheckboxGroupProps<T> extends Omit<CheckboxGroupProps<T>, 'onClick'> {
    onClick?: (value: T[]) => void
    identifier?: string
    dataTestId?: string
    showError?: boolean // set false if want to use external error state to control the error display
    validateByTouch?: boolean // set to false if you want to use an external error state to control error display. It will show errors without requiring the field to be touched.
}

const FormikCheckboxGroup = <T extends string | number>(props: FormikCheckboxGroupProps<T>) => {
    const { name, onClick, showError = true, validateByTouch = true } = props
    const [field, meta] = useField<T[]>(name)
    const { setFieldValue, setFieldTouched } = useFormikContext()
    const hasError = showError && !!meta.error && (!validateByTouch || meta.touched)

    const fieldValue = field.value || []

    const handleChecked = (optionValue: T): void => {
        let next
        const isExist = fieldValue.some(selected => _.isEqual(selected, optionValue))
        if (isExist) {
            next = fieldValue.filter(selected => !_.isEqual(selected, optionValue))
        } else {
            next = fieldValue.concat([optionValue])
        }
        setFieldValue(field.name as never, next)
        if (!meta.touched) setFieldTouched(field.name as never, true, false)
        if (onClick) onClick(next)
    }

    return (
        <CheckboxGroup
            {...props}
            onClick={handleChecked}
            errorMessage={(hasError && meta.error) || ''}
            selectedOptions={fieldValue}
        />
    )
}

export { FormikCheckboxGroup }
export default CheckboxGroup
